// Pure progress + visibility helpers shared between the API Worker and the
// web app. Operates on minimal structural types so each caller can pass its
// own (DB row vs frontend) shapes without conversion.

import { isSignatureDataUrl } from './signatureFormat.ts';

export type ProgressAdoptionTrigger = {
  trigger_value: string;
  document_id: string;
};

// Recursive AND/OR visibility expression. The zod schema in schemas.ts is
// typed against this — progress.ts stays dependency-free.
export type VisibilityRuleValue = string | number | boolean | string[];
export type VisibilityRule =
  | { q: string; value: VisibilityRuleValue }
  | { op: 'and' | 'or'; children: VisibilityRule[] };

export type ProgressQuestion = {
  id: string;
  type: string;
  required: boolean;
  requires_upload: boolean;
  parent_id: string | null;
  condition: { value: unknown } | null;
  visibility_rule: VisibilityRule | null;
  adoptable_doc_id: string | null;
  adoption_triggers: ProgressAdoptionTrigger[];
  video_url?: string | null;
};

export type ProgressSubmission = {
  question_id: string;
  answer: unknown;
  submission_files: { id: string; option_key?: string | null }[];
  // requires_signature is RESOLVED AT READ TIME from the adopted document, not
  // stored on the adoption row. That is what makes the document's flag
  // retroactive: an entity that adopted before the flag was turned on is
  // re-evaluated on the next read rather than grandfathered in. mode
  // distinguishes an adopted row (signs OUR document) from an uploaded
  // substitute (the entity's own equivalent document, which is never
  // signed). All fields optional so callers that predate signatures (and
  // every existing test fixture) still type-check and behave as before.
  submission_adoptions: {
    document_id: string;
    option_key?: string | null;
    mode?: 'adopted' | 'uploaded';
    signer_name?: string | null;
    signature_data_url?: string | null;
    requires_signature?: boolean | undefined;
  }[];
};

export type CompletionReason =
  | 'no_answer'
  | 'upload_required'
  | 'adoption_required'
  | 'signature_required'
  | 'video_unwatched';

export type ProgressResult = {
  progress: number;
  totalRequired: number;
  completedRequired: number;
  visibleIds: Set<string>;
  missing: Array<{ question_id: string; reason: CompletionReason }>;
};

export function matchesCondition(answer: unknown, expected: unknown): boolean {
  if (Array.isArray(expected)) {
    if (!Array.isArray(answer)) return false;
    return expected.every((v) => (answer as unknown[]).includes(v));
  }
  if (Array.isArray(answer)) return (answer as unknown[]).includes(expected);
  return answer === expected;
}

export function isVisible(
  q: ProgressQuestion,
  byId: Map<string, ProgressQuestion>,
  subByQ: Map<string, ProgressSubmission>,
  visiting: Set<string> = new Set(),
): boolean {
  // Cycle backstop: the API rejects circular dependencies at write time; if
  // legacy bad data contains one anyway, hide the question rather than
  // recurse forever. Hidden is the safe failure mode (can't block progress).
  if (visiting.has(q.id)) return false;
  visiting.add(q.id);
  try {
    if (q.visibility_rule) return evalRule(q.visibility_rule, byId, subByQ, visiting);
    if (!q.parent_id) return true;
    const parent = byId.get(q.parent_id);
    if (!parent) return false;
    if (!isVisible(parent, byId, subByQ, visiting)) return false;
    const parentSub = subByQ.get(parent.id);
    if (!parentSub) return false;
    if (!q.condition) return true;
    return matchesCondition(parentSub.answer, q.condition.value);
  } finally {
    visiting.delete(q.id);
  }
}

// A leaf is true iff the referenced question is itself visible, answered,
// and the answer matches (same equals/contains semantics as the legacy
// single-parent path). Unknown refs are false — deletion pruning in the API
// should make that unreachable.
function evalRule(
  rule: VisibilityRule,
  byId: Map<string, ProgressQuestion>,
  subByQ: Map<string, ProgressSubmission>,
  visiting: Set<string>,
): boolean {
  if ('op' in rule) {
    // Empty groups are schema-forbidden; if bad data slips through, hide
    // (consistent with the cycle backstop's safe failure mode).
    if (rule.children.length === 0) return false;
    return rule.op === 'and'
      ? rule.children.every((c) => evalRule(c, byId, subByQ, visiting))
      : rule.children.some((c) => evalRule(c, byId, subByQ, visiting));
  }
  const ref = byId.get(rule.q);
  if (!ref) return false;
  if (!isVisible(ref, byId, subByQ, visiting)) return false;
  const sub = subByQ.get(ref.id);
  if (!sub) return false;
  return matchesCondition(sub.answer, rule.value);
}

export function visibleQuestionSet(
  questions: ProgressQuestion[],
  subByQ: Map<string, ProgressSubmission>,
): Set<string> {
  const byId = new Map<string, ProgressQuestion>();
  for (const q of questions) byId.set(q.id, q);
  const visible = new Set<string>();
  for (const q of questions) {
    if (isVisible(q, byId, subByQ)) visible.add(q.id);
  }
  return visible;
}

/**
 * Does this adoption row satisfy its document's signing requirement?
 *
 * An uploaded substitute (mode: 'uploaded') always satisfies - you sign the
 * document you adopt, not one you supplied yourself, so requires_signature on
 * the linked document is irrelevant to it. Otherwise, a document that needs
 * no signature always satisfies; one that does needs BOTH a non-blank signer
 * name and a real signature image - the DB CHECK in 0027 keeps the three
 * signature columns all-or-nothing, but a blank-ish name could still arrive
 * from a client that predates the trim in SubmissionAdoptionInput, so it is
 * re-checked here.
 *
 * The image is checked against the same pattern the write path validates and
 * the UI renders through (isSignatureDataUrl). Without it a value the viewer
 * refuses to display - an http:// tracker, a scriptable SVG - would still
 * count as signed, letting a submission pass the gate carrying no visible
 * signature. Anything written outside SubmissionAdoptionInput (a backfill, a
 * direct admin write) is caught here.
 */
export function adoptionSatisfies(adoption: {
  mode?: 'adopted' | 'uploaded';
  signer_name?: string | null;
  signature_data_url?: string | null;
  requires_signature?: boolean | undefined;
}): boolean {
  if (adoption.mode === 'uploaded') return true;
  if (!adoption.requires_signature) return true;
  return (
    typeof adoption.signer_name === 'string' &&
    adoption.signer_name.trim() !== '' &&
    isSignatureDataUrl(adoption.signature_data_url)
  );
}

/**
 * Is this adoption row one the question is CURRENTLY asking for, given the
 * stored answer? The same matching isQuestionComplete applies when it looks an
 * adoption up, so a row it would never consult (one left behind after the
 * answer moved off its trigger value) reads as inactive here too:
 *   - document_adopt / document_upload: the row for the question's own
 *     adoptable document, whatever the answer;
 *   - multiselect_uploads: the row keyed by a SELECTED option whose trigger
 *     names this document (the per-option option_key match);
 *   - any other type: a trigger whose value the answer matches names this
 *     document.
 * Rows the portal never deletes on an answer change (the adopt DELETE is a
 * manual action) linger, so anything that judges a live adoption — the
 * fixed-period expiry stamp and count (0062) — must filter through this.
 */
export function adoptionIsActive(
  q: Pick<ProgressQuestion, 'type' | 'adoptable_doc_id' | 'adoption_triggers'>,
  answer: unknown,
  adoption: { document_id: string; option_key?: string | null },
): boolean {
  if (q.type === 'document_adopt' || q.type === 'document_upload') {
    return q.adoptable_doc_id != null && adoption.document_id === q.adoptable_doc_id;
  }
  if (q.type === 'multiselect_uploads') {
    if (!Array.isArray(answer)) return false;
    const key = adoption.option_key ?? null;
    if (key === null || !(answer as unknown[]).some((opt) => String(opt) === key)) return false;
    return q.adoption_triggers.some(
      (t) => t.trigger_value === key && t.document_id === adoption.document_id,
    );
  }
  if (answer === null || answer === undefined) return false;
  return q.adoption_triggers.some(
    (t) => t.document_id === adoption.document_id && matchesCondition(answer, t.trigger_value),
  );
}

export function isQuestionComplete(
  q: ProgressQuestion,
  sub: ProgressSubmission | undefined,
): CompletionReason | null {
  if (q.type === 'informational') {
    // Informational questions without a video are always complete — they
    // are read-only content with no interaction needed. With a video,
    // the company must record that they watched it.
    if (!q.video_url) return null;
    if (!sub) return 'video_unwatched';
    const a = sub.answer as { watched_at?: unknown } | null | undefined;
    if (a && typeof a === 'object' && !Array.isArray(a) && typeof a.watched_at === 'string') {
      return null;
    }
    return 'video_unwatched';
  }

  if (q.type === 'image_info') {
    // Image-info questions are read-only display content (image + text)
    // and never require an answer.
    return null;
  }

  if (!sub) return 'no_answer';

  if (q.type === 'document_adopt') {
    if (!q.adoptable_doc_id) return null;
    const adoption = sub.submission_adoptions.find((a) => a.document_id === q.adoptable_doc_id);
    if (!adoption) return 'adoption_required';
    return adoptionSatisfies(adoption) ? null : 'signature_required';
  }

  if (q.type === 'document_upload') {
    // A document_upload question is satisfied by either an uploaded file or an
    // adoption row for the linked standard document (when one is configured).
    // An adoption that still owes a signature is NOT satisfied - but it also
    // must not fall through to 'upload_required', which would tell the vendor
    // to upload a file when what is missing is a signature.
    if (q.adoptable_doc_id) {
      const adoption = sub.submission_adoptions.find((a) => a.document_id === q.adoptable_doc_id);
      if (adoption) return adoptionSatisfies(adoption) ? null : 'signature_required';
    }
    return sub.submission_files.length > 0 ? null : 'upload_required';
  }

  if (q.type === 'signature') {
    if (!sub.answer || typeof sub.answer !== 'object' || Array.isArray(sub.answer)) {
      return 'no_answer';
    }
    const sig = sub.answer as {
      signature_data_url?: unknown;
      signer_name?: unknown;
      signed_at?: unknown;
    };
    if (
      typeof sig.signature_data_url !== 'string' ||
      sig.signature_data_url.length === 0 ||
      typeof sig.signer_name !== 'string' ||
      sig.signer_name.trim() === '' ||
      typeof sig.signed_at !== 'string' ||
      sig.signed_at.length === 0
    ) {
      return 'no_answer';
    }
    return null;
  }

  if (q.type === 'multiselect_uploads') {
    if (sub.answer === null || sub.answer === undefined) return 'no_answer';
    if (!Array.isArray(sub.answer) || sub.answer.length === 0) return 'no_answer';
    const triggers = new Map(q.adoption_triggers.map((t) => [t.trigger_value, t.document_id]));
    for (const opt of sub.answer as unknown[]) {
      const key = String(opt);
      const adoptableDoc = triggers.get(key);
      const adoption =
        adoptableDoc === undefined
          ? undefined
          : sub.submission_adoptions.find(
              (a) => a.document_id === adoptableDoc && (a.option_key ?? null) === key,
            );
      if (adoption) {
        if (!adoptionSatisfies(adoption)) return 'signature_required';
        continue;
      }
      const hasFile = sub.submission_files.some((f) => (f.option_key ?? null) === key);
      if (!hasFile) return 'upload_required';
    }
    return null;
  }

  if (sub.answer === null || sub.answer === undefined) return 'no_answer';
  if (typeof sub.answer === 'string' && sub.answer.trim() === '') return 'no_answer';
  if (Array.isArray(sub.answer) && sub.answer.length === 0) return 'no_answer';

  if (q.requires_upload && sub.submission_files.length === 0) return 'upload_required';

  for (const trigger of q.adoption_triggers) {
    if (matchesCondition(sub.answer, trigger.trigger_value)) {
      const adoption = sub.submission_adoptions.find(
        (a) => a.document_id === trigger.document_id,
      );
      if (!adoption) return 'adoption_required';
      if (!adoptionSatisfies(adoption)) return 'signature_required';
    }
  }
  return null;
}

// ----------------------------------------------------------------------
// Company information completeness
// ----------------------------------------------------------------------

// Fields the company must populate before they can submit for review.
// trustee_info is omitted because it is only required "if applicable".
export const REQUIRED_COMPANY_INFO_FIELDS = [
  'abn',
  'primary_contact_name',
  'contact_role',
  'contact_phone',
  'contact_email',
  'employee_count',
] as const;

export type RequiredCompanyInfoField = (typeof REQUIRED_COMPANY_INFO_FIELDS)[number];

export type CompanyInfoForGate = Partial<Record<RequiredCompanyInfoField, unknown>>;

export function missingCompanyInfoFields(
  info: CompanyInfoForGate,
): RequiredCompanyInfoField[] {
  const missing: RequiredCompanyInfoField[] = [];
  for (const field of REQUIRED_COMPANY_INFO_FIELDS) {
    const value = info[field];
    if (value === null || value === undefined) {
      missing.push(field);
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') missing.push(field);
  }
  return missing;
}

// ----------------------------------------------------------------------
// Driver information completeness
// ----------------------------------------------------------------------

// Fields a driver must populate before they can submit their questionnaire.
// The DB columns are all nullable (0028) — every existing driver row predates
// them — so this, checked by POST /api/driver/me/submit, is the only thing that
// makes them required. Mirrors REQUIRED_COMPANY_INFO_FIELDS above.
//
// address_line2 and emergency_contact_relationship are omitted deliberately: a
// unit address may have no second line, and a relationship label is useful but
// not load-bearing.
export const REQUIRED_DRIVER_INFO_FIELDS = [
  'address_line1',
  'address_suburb',
  'address_state',
  'address_postcode',
  'emergency_contact_name',
  'emergency_contact_phone',
  // photo_path (0045) is likewise nullable in the DB — admins can still create a
  // driver without one, which keeps bulk-add and the Locate2u sync working. Its
  // requiredness is submit-gate-only, exactly like the fields above.
  'photo_path',
] as const;

export type RequiredDriverInfoField = (typeof REQUIRED_DRIVER_INFO_FIELDS)[number];

export type DriverInfoForGate = Partial<Record<RequiredDriverInfoField, unknown>>;

export function missingDriverInfoFields(info: DriverInfoForGate): RequiredDriverInfoField[] {
  const missing: RequiredDriverInfoField[] = [];
  for (const field of REQUIRED_DRIVER_INFO_FIELDS) {
    const value = info[field];
    if (value === null || value === undefined) {
      missing.push(field);
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') missing.push(field);
  }
  return missing;
}

export function computeProgress(
  questions: ProgressQuestion[],
  submissions: ProgressSubmission[],
): ProgressResult {
  const subByQ = new Map<string, ProgressSubmission>();
  for (const s of submissions) subByQ.set(s.question_id, s);

  const visibleIds = visibleQuestionSet(questions, subByQ);

  let totalRequired = 0;
  let completedRequired = 0;
  const missing: ProgressResult['missing'] = [];

  for (const q of questions) {
    if (!visibleIds.has(q.id)) continue;
    if (!q.required) continue;
    totalRequired += 1;
    const reason = isQuestionComplete(q, subByQ.get(q.id));
    if (reason === null) {
      completedRequired += 1;
    } else {
      missing.push({ question_id: q.id, reason });
    }
  }

  const progress =
    totalRequired === 0 ? 100 : Math.round((completedRequired * 100) / totalRequired);
  return { progress, totalRequired, completedRequired, visibleIds, missing };
}
